import { DialogContentText } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import {FC, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormButton, ToastSeverity, useToast } from 'thermia-react-container';
import { ApiErrorHelper } from '../../../../validation/Errors';
import { AdministrationCompletedForTable } from './adminRegistrationsTable';
import {useRegistrationService} from "../../../../service/registrationService";

interface Props {
  selectedRegistration?: AdministrationCompletedForTable;
  handleCancel: () => void;
  updateList: (id: string) => Promise<void>;
}

type PageStatus = 'init' | 'success' | 'error' | 'locked';

const ReopenForm: FC<Props> = ({ handleCancel, selectedRegistration, updateList }) => {
  const { reopenCompletedRegistration } = useRegistrationService(true);
  const { addToast } = useToast();

  const [pageStatus, setPageStatus] = useState<PageStatus>('init');
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation('registrations');

  const handleReopenRegistration = async () => {
    if (!selectedRegistration?.id) {
      return;
    }
    setLoading(true);
    try {
      await reopenCompletedRegistration(selectedRegistration.id);
      await updateList(selectedRegistration.id);
      setPageStatus('success');
    } catch (e) {
      if (ApiErrorHelper.isOnsiteVersionInvalid(e)) {
        addToast(t('registrations.general.errorOnsiteVersionIsInvalid'), ToastSeverity.ERROR);
      } else {
        setPageStatus('error');
      }
    }
    setLoading(false);
  };

  const closeModal = async () => {
    handleCancel();
  };

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center">
      {pageStatus === 'init' && (
        <>
          <DialogTitle>{t('registrations.admin.adminEditCompletedRegistration')}</DialogTitle>
          <DialogContent>
            <DialogContentText paragraph>{t('registrations.admin.adminEditCompletedRegistrationQuestion')}?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} title={t('registrations.general.no')} />
            <FormButton
              variant={'contained'}
              onClick={handleReopenRegistration}
              title={t('registrations.general.yes')}
              loading={loading}
              style={{ marginLeft: '0px' }}
            />
          </DialogActions>
        </>
      )}

      {pageStatus === 'success' && (
        <>
          <DialogTitle>{t('registrations.general.success')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('registrations.admin.adminEditCompletedRegistrationSuccessDescription')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button title={t('registrations.general.ok')} variant={'contained'} onClick={closeModal} />
          </DialogActions>
        </>
      )}
      {pageStatus === 'error' && (
        <>
          <DialogTitle>{t('registrations.general.somethingWentWrong')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('registrations.admin.editCompletedRegistrationErrorDescription')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button title={t('registrations.general.ok')} onClick={handleCancel} />
          </DialogActions>
        </>
      )}

      {pageStatus === 'locked' && (
        <>
          <DialogTitle>{t('registrations.general.editCompletedRegistrationLocked')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('registrations.admin.editCompletedRegistrationLockedDescription')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button title={t('registrations.general.ok')} onClick={handleCancel} variant={'contained'} />
          </DialogActions>
        </>
      )}
    </Grid>
  );
};

export default ReopenForm;
